import React from "react";
import { Box, Paper } from "@mui/material";
import MenuBookIcon from "@mui/icons-material/MenuBook";

import { SectionTitle } from "./StyledComponents";

const RecipeInstructions = ({ instructions }) => {
  if (!instructions) {
    return null;
  }

  // Guard: if instructions is a string, treat as single step
  let steps = [];
  if (Array.isArray(instructions)) {
    steps = instructions.filter(
      (step) => step && step.instruction && step.instruction.trim() !== "",
    );
  } else if (typeof instructions === "string" && instructions.trim() !== "") {
    steps = [{ instruction: instructions }];
  }

  if (steps.length === 0) {
    return null;
  }

  // Calculate total duration
  const totalDuration = steps.reduce(
    (sum, step) => sum + (step.duration ? Number(step.duration) : 0),
    0,
  );
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    if (min && sec) return `${min} min ${sec} sec`;
    if (min) return `${min} min`;
    return `${sec} sec`;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle variant="h5">
        <MenuBookIcon />
        Instructions
      </SectionTitle>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          p: 2,
          mt: 2,
          backgroundColor: "background.paper",
        }}
      >
        {totalDuration > 0 && (
          <Box
            sx={{
              mb: 3,
              fontWeight: 500,
              color: "text.secondary",
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.04)",
              borderRadius: 1,
              p: 1.5,
            }}
          >
            <Box component="span" sx={{ mr: 1 }}>
              ⏱️
            </Box>
            Total Cooking Time: {formatDuration(totalDuration)}
          </Box>
        )}
        <Box sx={{ pl: 0, mb: 0 }}>
          {steps.map((step, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                mb: 2.5,
                pb: idx < steps.length - 1 ? 2.5 : 0,
                borderBottom:
                  idx < steps.length - 1
                    ? "1px dashed rgba(0,0,0,0.12)"
                    : "none",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  color: "primary.contrastText",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  mr: 2,
                  flexShrink: 0,
                  fontSize: "0.875rem",
                }}
              >
                {idx + 1}
              </Box>
              <Box sx={{ pt: 0.5 }}>
                <Box
                  sx={{
                    lineHeight: 1.6,
                    color: "text.primary",
                    fontSize: "1rem",
                  }}
                >
                  {step.instruction}
                </Box>
                {step.duration ? (
                  <Box
                    sx={{
                      color: "text.secondary",
                      mt: 0.5,
                      fontSize: "0.875rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        mr: 0.5,
                        opacity: 0.7,
                        fontSize: "0.75rem",
                      }}
                    >
                      ⏱️
                    </Box>
                    {formatDuration(Number(step.duration))}
                  </Box>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default RecipeInstructions;
